import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/homeData';

export default function HeroSection() {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (category: typeof categories[0]) => {
    setSelectedCategory(category.name);
    setIsOpen(false);
    window.open(category.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <section className="relative w-full min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Elegant%20fashion%20boutique%20interior%20with%20warm%20amber%20lighting%2C%20luxury%20clothing%20racks%20displaying%20premium%20garments%20in%20neutral%20earth%20tones%2C%20soft%20golden%20ambient%20light%20creating%20cozy%20atmosphere%2C%20editorial%20fashion%20photography%20style%20with%20shallow%20depth%20of%20field%20and%20artistic%20composition&width=1600&height=900&seq=hero-sugargoo-v2&orientation=landscape"
          alt="Sugargoo Spreadsheet Resource Hub"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/45"></div>
      </div>

      <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 py-32 md:py-40 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-background-50/90 backdrop-blur-sm rounded-full mb-6 md:mb-8">
          <span className="w-2 h-2 bg-accent-500 rounded-full animate-pulse"></span>
          <span className="text-xs md:text-sm font-medium text-foreground-700">
            Updated Daily · Independent Resource
          </span>
        </div>

        <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 leading-tight mb-4 md:mb-6">
          The Ultimate <span className="text-primary-300">Sugargoo Spreadsheet</span> Resource Hub
        </h1>

        <p className="text-sm md:text-lg text-background-100/90 max-w-2xl mx-auto mb-8 md:mb-10 leading-relaxed">
          Discover thousands of organized Sugargoo Spreadsheet finds, product recommendations, shopping guides, shipping information and expert reviews — all in one place.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 mb-8 md:mb-10">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 font-semibold text-sm md:text-base rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg shadow-primary-500/25"
          >
            Sugargoo Spreadsheet
            <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-background-50/95 backdrop-blur-sm text-foreground-900 font-semibold text-sm md:text-base rounded-full hover:bg-background-50 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Buy It Now
            <i className="ri-shopping-bag-3-line w-4 h-4 flex items-center justify-center"></i>
          </a>
        </div>

        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="inline-flex items-center justify-between gap-3 w-full max-w-md px-5 py-3.5 bg-background-50/90 backdrop-blur-sm border border-background-200/50 rounded-2xl text-left text-sm text-foreground-700 hover:border-background-300/70 transition-all duration-200 cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <i className="ri-grid-line w-4 h-4 flex items-center justify-center text-primary-500"></i>
              <span>{selectedCategory || 'Browse Product Categories'}</span>
            </span>
            <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}></i>
          </button>

          {isOpen && (
            <div className="absolute top-full left-0 right-0 mt-2 max-w-md mx-auto bg-background-50 rounded-2xl border border-background-200/60 shadow-lg overflow-hidden z-50 max-h-80 overflow-y-auto">
              {categories.map((cat) => (
                <button
                  key={cat.name}
                  onClick={() => handleSelect(cat)}
                  className="w-full flex items-center gap-3 px-5 py-3 text-sm text-foreground-700 hover:bg-background-100 transition-colors duration-150 text-left cursor-pointer whitespace-nowrap"
                >
                  <i className={`${cat.icon} w-4 h-4 flex items-center justify-center text-primary-500`}></i>
                  <span className="flex-1">{cat.name}</span>
                  <i className="ri-arrow-right-up-line w-3.5 h-3.5 flex items-center justify-center text-foreground-400"></i>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 mt-8 md:mt-10">
          {[
            { icon: 'ri-checkbox-circle-line', text: 'Updated Spreadsheet Collections' },
            { icon: 'ri-guide-line', text: 'Verified Shopping Guides' },
            { icon: 'ri-ship-line', text: 'Shipping Cost Information' },
            { icon: 'ri-shield-check-line', text: 'Safety & Review Resources' },
          ].map((item) => (
            <div key={item.text} className="flex items-center gap-1.5 text-xs md:text-sm text-background-100/80">
              <i className={`${item.icon} w-3.5 h-3.5 flex items-center justify-center text-accent-300`}></i>
              <span>{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}